import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
} from "react-router-dom";

// Contexts
import { AuthProvider, ROLES } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { SelectedCityProvider } from "./contexts/SelectedCityContext";

// Components
import { PrivateRoute, PublicRoute, RoleBasedRoute } from "./components/RoleBasedRoute";
import { PrivateLayout } from "./components";
import PageMeta from "./components/PageMeta";

// Pages
import Login from "./pages/login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Autocadastro from "./pages/Autocadastro";
import Dashboard from "./pages/Dashboard";
import Usuarios from "./pages/Usuarios";
import AutocadastroLinks from "./pages/AutocadastroLinks";
import Escolas from "./pages/Escolas";
import Relatorios from "./pages/Relatorios";
import Configuracoes from "./pages/Configuracoes";
import UsuariosAcessos from "./pages/UsuariosAcessos";
import Inputs from "./pages/Inputs";
import Provas from "./pages/Provas";
import GerentesEscolas from "./pages/GerentesEscolas";
import Turmas from "./pages/Turmas";
import Professores from "./pages/Professores";
import ProfessorPainel from "./pages/ProfessorPainel";
import Funcionarios from "./pages/Funcionarios";
import FrequenciaFuncionarios from "./pages/FrequenciaFuncionarios";
import Alunos from "./pages/Alunos";
import StudentProfile from "./pages/StudentProfile";
import AlunosTEA from "./pages/AlunosTEA";
import Gabaritos from "./pages/Gabaritos";
import RespostasAlunos from "./pages/RespostasAlunos";
import Descritores from "./pages/Descritores";
import SimuladorFinanceiro from "./pages/SimuladorFinanceiro";
import DiarioTurma from "./pages/DiarioTurma";
import GradeHoraria from "./pages/GradeHoraria";
import GradeHorariaTurmas from "./pages/GradeHorariaTurmas";
import AcompanhamentoDiarios from "./pages/AcompanhamentoDiarios";
import MeuPainel from "./pages/MeuPainel";
import CalendarioLetivo from "./pages/CalendarioLetivo";
import IdebMunicipal from "./pages/IdebMunicipal";
import Perfil from "./pages/Perfil";
import TrocarSenhaObrigatoria from "./pages/TrocarSenhaObrigatoria";
import Homepage from "./pages/Homepage";
import DashboardDemo from "./pages/DashboardDemo";
import NotFound from "./components/NotFound";
import HP from './pages/exemples/HP';

/** Redireciona a rota antiga de Frequência para a aba Frequência do Diário.
 *
 * Links antigos da Grade Horária vinham com `?data=&aula=` apontando para uma
 * aula específica: os parâmetros vão junto, e a aba já abre naquela aula.
 */
function RedirecionaFrequenciaParaDiario() {
  const { id } = useParams();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const data = params.get('data');
  const aula = params.get('aula');

  const destino = new URLSearchParams({ tab: 'frequencia' });
  if (data) destino.set('data', data);
  if (aula) destino.set('aula', aula);
  return <Navigate to={`/turmas/${id}/diario?${destino}`} replace />;
}

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <SelectedCityProvider>
        <Router>
          <Routes>
            {/* ============================================
                ROTAS PÚBLICAS (sem sidebar)
                ============================================ */}

            {/* Homepage - página de vendas */}
            <Route path="/" element={<PageMeta title="Início"><Homepage /></PageMeta>} />

            {/* Dashboard Demo - dados mockados para demonstração */}
            <Route path="/demo" element={<PageMeta title="Demo"><DashboardDemo /></PageMeta>} />

            {/* Nova Rota HP - Exemplo de Landing Page Refatorada */}
            <Route path="/hp" element={<PageMeta title="HP - Preview"><HP /></PageMeta>} />

            {/* Login */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <PageMeta title="Login"><Login /></PageMeta>
                </PublicRoute>
              }
            />

            {/* Esqueci a senha */}
            <Route
              path="/forgot-password"
              element={
                <PublicRoute>
                  <PageMeta title="Esqueci a Senha"><ForgotPassword /></PageMeta>
                </PublicRoute>
              }
            />

            {/* Redefinir senha */}
            <Route
              path="/reset-password"
              element={
                <PublicRoute>
                  <PageMeta title="Redefinir Senha"><ResetPassword /></PageMeta>
                </PublicRoute>
              }
            />

            {/* Autocadastro por link (secretário, supervisor, diretor). Fora
                do PublicRoute: quem já está logado também pode abrir o link
                para conferir o que a pessoa vai ver. */}
            <Route
              path="/cadastro/:token"
              element={<PageMeta title="Cadastro"><Autocadastro /></PageMeta>}
            />

            {/* Troca de senha obrigatória (primeiro acesso / reset por admin).
                Fica fora do PrivateLayout para esconder o sidebar até o
                usuário definir uma nova senha. */}
            <Route
              path="/trocar-senha"
              element={
                <PageMeta title="Trocar Senha"><TrocarSenhaObrigatoria /></PageMeta>
              }
            />

            {/* ============================================
                ROTAS PRIVADAS (com sidebar via PrivateLayout)
                ============================================ */}
            <Route
              element={
                <PrivateRoute>
                  <PrivateLayout />
                </PrivateRoute>
              }
            >
              {/* Dashboard - todos os usuarios autenticados */}
              <Route path="/dashboard" element={<PageMeta title="Dashboard"><Dashboard /></PageMeta>} />

              {/* Usuarios - Admin, Supervisor, Coordenador e Referência Técnica.
                  Coordenador/Secretário gerenciam apenas usuários do próprio
                  município; o escopo é garantido no backend. */}
              <Route
                path="/usuarios"
                element={
                  <RoleBasedRoute allowedRoles={[ROLES.ADMIN, ROLES.SUPERVISOR, ROLES.COORDENADOR, ROLES.REFERENCIA_TECNICA]}>
                    <PageMeta title="Usuários"><Usuarios /></PageMeta>
                  </RoleBasedRoute>
                }
              />

              {/* Autocadastro - quem gera links e aprova: Admin e Coordenador */}
              <Route
                path="/usuarios/autocadastro"
                element={
                  <RoleBasedRoute allowedRoles={[ROLES.ADMIN, ROLES.COORDENADOR]} deniedRoles={[ROLES.SECRETARIO]}>
                    <PageMeta title="Autocadastro"><AutocadastroLinks /></PageMeta>
                  </RoleBasedRoute>
                }
              />

              {/* Escolas - todos exceto Gestor (Professor: apenas a sua) */}
              <Route
                path="/escolas"
                element={
                  <RoleBasedRoute allowedRoles={[ROLES.ADMIN, ROLES.SUPERVISOR, ROLES.COORDENADOR, ROLES.REFERENCIA_TECNICA, ROLES.PROFESSOR]}>
                    <PageMeta title="Escolas"><Escolas /></PageMeta>
                  </RoleBasedRoute>
                }
              />

              {/* Turmas - todos exceto Gestor (Professor: só as próprias) */}
              <Route
                path="/turmas"
                element={
                  <RoleBasedRoute allowedRoles={[ROLES.ADMIN, ROLES.SUPERVISOR, ROLES.COORDENADOR, ROLES.REFERENCIA_TECNICA, ROLES.PROFESSOR]}>
                    <PageMeta title="Turmas"><Turmas /></PageMeta>
                  </RoleBasedRoute>
                }
              />

              {/* Professores - Admin/Supervisor/Coordenador/RT (Professor não vê listagem) */}
              <Route
                path="/professores"
                element={
                  <RoleBasedRoute allowedRoles={[ROLES.ADMIN, ROLES.SUPERVISOR, ROLES.COORDENADOR, ROLES.REFERENCIA_TECNICA]}>
                    <PageMeta title="Professores"><Professores /></PageMeta>
                  </RoleBasedRoute>
                }
              />

              {/* Painel do professor: a coordenação acompanha o que ele lançou */}
              <Route
                path="/professores/:id"
                element={
                  <RoleBasedRoute allowedRoles={[ROLES.ADMIN, ROLES.SUPERVISOR, ROLES.COORDENADOR, ROLES.REFERENCIA_TECNICA]}>
                    <PageMeta title="Painel do professor"><ProfessorPainel /></PageMeta>
                  </RoleBasedRoute>
                }
              />

              {/* RH - Funcionários - Admin, Coordenador e Referência Técnica */}
              <Route
                path="/funcionarios"
                element={
                  <RoleBasedRoute allowedRoles={[ROLES.ADMIN, ROLES.COORDENADOR, ROLES.REFERENCIA_TECNICA]} deniedRoles={[ROLES.SECRETARIO]}>
                    <PageMeta title="Funcionários (RH)"><Funcionarios /></PageMeta>
                  </RoleBasedRoute>
                }
              />

              {/* Frequência de Funcionários - Admin e Coordenador */}
              <Route
                path="/funcionarios/frequencia"
                element={
                  <RoleBasedRoute allowedRoles={[ROLES.ADMIN, ROLES.COORDENADOR]} deniedRoles={[ROLES.SECRETARIO]}>
                    <PageMeta title="Frequência de Funcionários"><FrequenciaFuncionarios /></PageMeta>
                  </RoleBasedRoute>
                }
              />

              {/* A antiga tela de Frequência da turma virou a aba "Chamada do
                  Dia" do Diário — um lugar só para lançar frequência, agora
                  por aula (disciplina + horário). A rota continua respondendo
                  para não quebrar link salvo/favorito. */}
              <Route
                path="/turmas/:id/frequencia"
                element={<RedirecionaFrequenciaParaDiario />}
              />

              {/* Atalho do menu: escolhe a turma e entra na grade. Com uma
                  turma só, vai direto. É o caminho diário do professor, porque
                  é da grade que ele abre a chamada da própria aula. */}
              <Route
                path="/grade-horaria"
                element={
                  <RoleBasedRoute allowedRoles={[ROLES.ADMIN, ROLES.COORDENADOR, ROLES.REFERENCIA_TECNICA, ROLES.SUPERVISOR, ROLES.PROFESSOR]}>
                    <PageMeta title="Grade Horária"><GradeHorariaTurmas /></PageMeta>
                  </RoleBasedRoute>
                }
              />

              {/* Mesmo atalho para o Diário: o item do menu também apontava
                  para /turmas com "Acesse via Turmas > Diário". */}
              <Route
                path="/diario"
                element={
                  <RoleBasedRoute allowedRoles={[ROLES.ADMIN, ROLES.COORDENADOR, ROLES.REFERENCIA_TECNICA, ROLES.SUPERVISOR, ROLES.PROFESSOR]}>
                    <PageMeta title="Diário de Turma">
                      <GradeHorariaTurmas destino="diario" titulo="Diário de Turma" />
                    </PageMeta>
                  </RoleBasedRoute>
                }
              />

              {/* Grade Horária da Turma - define qual disciplina ocupa cada aula
                  do dia. Admin/Coordenador/Ref. Técnica montam; Professor e
                  Supervisor apenas consultam (canEdit em GradeHoraria). */}
              <Route
                path="/turmas/:id/grade-horaria"
                element={
                  <RoleBasedRoute allowedRoles={[ROLES.ADMIN, ROLES.COORDENADOR, ROLES.REFERENCIA_TECNICA, ROLES.SUPERVISOR, ROLES.PROFESSOR]}>
                    <PageMeta title="Grade Horária"><GradeHoraria /></PageMeta>
                  </RoleBasedRoute>
                }
              />

              {/* Calendário Letivo - dias sem aula. Sem ele, feriado vira
                  pendência em massa no acompanhamento. */}
              <Route
                path="/diarios/calendario-letivo"
                element={
                  <RoleBasedRoute allowedRoles={[ROLES.ADMIN, ROLES.COORDENADOR, ROLES.REFERENCIA_TECNICA, ROLES.SUPERVISOR, ROLES.PROFESSOR]}>
                    <PageMeta title="Calendário Letivo"><CalendarioLetivo /></PageMeta>
                  </RoleBasedRoute>
                }
              />

              {/* Acompanhamento de Diários - painel da coordenação com as
                  aulas previstas x lançadas, dia a dia. */}
              <Route
                path="/diarios/acompanhamento"
                element={
                  <RoleBasedRoute allowedRoles={[ROLES.ADMIN, ROLES.COORDENADOR, ROLES.SUPERVISOR, ROLES.REFERENCIA_TECNICA, ROLES.PROFESSOR]}>
                    <PageMeta title="Acompanhamento de Diários"><AcompanhamentoDiarios /></PageMeta>
                  </RoleBasedRoute>
                }
              />

              {/* Painel da Rede - virou a aba "Diário" do Dashboard. O endereço
                  antigo continua valendo para favoritos e links salvos. */}
              <Route
                path="/diarios/painel-rede"
                element={<Navigate to="/dashboard?aba=diario" replace />}
              />

              {/* Meu Painel - cockpit do professor sobre a sua turma. */}
              <Route
                path="/meu-painel"
                element={
                  <RoleBasedRoute allowedRoles={[ROLES.PROFESSOR, ROLES.ADMIN]}>
                    <PageMeta title="Meu Painel"><MeuPainel /></PageMeta>
                  </RoleBasedRoute>
                }
              />

              {/* Diário da Turma - todos exceto Gestor. Professor escreve; Coordenador,
                  Referência Técnica e Supervisor têm acesso somente leitura (canEdit
                  no DiarioTurma libera edição só para Admin e Professor). */}
              <Route
                path="/turmas/:id/diario"
                element={
                  <RoleBasedRoute allowedRoles={[ROLES.ADMIN, ROLES.SUPERVISOR, ROLES.COORDENADOR, ROLES.REFERENCIA_TECNICA, ROLES.PROFESSOR]}>
                    <PageMeta title="Diário da Turma"><DiarioTurma /></PageMeta>
                  </RoleBasedRoute>
                }
              />

              {/* Alunos - todos exceto Gestor */}
              <Route
                path="/alunos"
                element={
                  <RoleBasedRoute allowedRoles={[ROLES.ADMIN, ROLES.SUPERVISOR, ROLES.COORDENADOR, ROLES.REFERENCIA_TECNICA, ROLES.PROFESSOR]}>
                    <PageMeta title="Alunos"><Alunos /></PageMeta>
                  </RoleBasedRoute>
                }
              />

              {/* Perfil do Aluno - todos exceto Gestor */}
              <Route
                path="/alunos/:id"
                element={
                  <RoleBasedRoute allowedRoles={[ROLES.ADMIN, ROLES.SUPERVISOR, ROLES.COORDENADOR, ROLES.REFERENCIA_TECNICA, ROLES.PROFESSOR]}>
                    <PageMeta title="Perfil do Aluno"><StudentProfile /></PageMeta>
                  </RoleBasedRoute>
                }
              />

              {/* Alunos AEE - todos exceto Gestor */}
              <Route
                path="/alunos-tea"
                element={
                  <RoleBasedRoute allowedRoles={[ROLES.ADMIN, ROLES.SUPERVISOR, ROLES.COORDENADOR, ROLES.REFERENCIA_TECNICA, ROLES.PROFESSOR]}>
                    <PageMeta title="Alunos AEE"><AlunosTEA /></PageMeta>
                  </RoleBasedRoute>
                }
              />

              {/* Perfil do Aluno AEE - redireciona para perfil unificado */}
              <Route
                path="/alunos-tea/:id"
                element={
                  <RoleBasedRoute allowedRoles={[ROLES.ADMIN, ROLES.SUPERVISOR, ROLES.COORDENADOR, ROLES.REFERENCIA_TECNICA, ROLES.PROFESSOR]}>
                    <PageMeta title="Perfil do Aluno"><StudentProfile /></PageMeta>
                  </RoleBasedRoute>
                }
              />

              {/* Relatorios - todos os perfis pedagógicos */}
              <Route
                path="/relatorios"
                element={
                  <RoleBasedRoute allowedRoles={[ROLES.ADMIN, ROLES.GESTOR, ROLES.SUPERVISOR, ROLES.COORDENADOR, ROLES.REFERENCIA_TECNICA, ROLES.PROFESSOR]}>
                    <PageMeta title="Relatórios"><Relatorios /></PageMeta>
                  </RoleBasedRoute>
                }
              />

              {/* Configuracoes - apenas Admin */}
              <Route
                path="/configuracoes"
                element={
                  <RoleBasedRoute allowedRoles={[ROLES.ADMIN]}>
                    <PageMeta title="Configurações"><Configuracoes /></PageMeta>
                  </RoleBasedRoute>
                }
              />

              {/* Usuários e acessos - apenas Admin (Configurações › Visão Geral) */}
              <Route
                path="/configuracoes/usuarios"
                element={
                  <RoleBasedRoute allowedRoles={[ROLES.ADMIN]}>
                    <PageMeta title="Usuários e acessos"><UsuariosAcessos /></PageMeta>
                  </RoleBasedRoute>
                }
              />

              {/* Gerentes-Escolas - Admin/Coordenador (RT em leitura) */}
              <Route
                path="/gerentes-escolas"
                element={
                  <RoleBasedRoute allowedRoles={[ROLES.ADMIN, ROLES.COORDENADOR, ROLES.REFERENCIA_TECNICA]} deniedRoles={[ROLES.SECRETARIO]}>
                    <PageMeta title="Gerentes de Escolas"><GerentesEscolas /></PageMeta>
                  </RoleBasedRoute>
                }
              />

              {/* Inputs/Upload - Admin/Coordenador */}
              <Route
                path="/inputs"
                element={
                  <RoleBasedRoute allowedRoles={[ROLES.ADMIN, ROLES.COORDENADOR, ROLES.REFERENCIA_TECNICA]} deniedRoles={[ROLES.SECRETARIO]}>
                    <PageMeta title="Upload"><Inputs /></PageMeta>
                  </RoleBasedRoute>
                }
              />

              {/* Upload - alias para /inputs */}
              <Route
                path="/upload"
                element={
                  <RoleBasedRoute allowedRoles={[ROLES.ADMIN, ROLES.COORDENADOR, ROLES.REFERENCIA_TECNICA]} deniedRoles={[ROLES.SECRETARIO]}>
                    <PageMeta title="Upload"><Inputs /></PageMeta>
                  </RoleBasedRoute>
                }
              />

              {/* Provas - Admin/Coordenador */}
              <Route
                path="/provas"
                element={
                  <RoleBasedRoute allowedRoles={[ROLES.ADMIN, ROLES.COORDENADOR]}>
                    <PageMeta title="Provas"><Provas /></PageMeta>
                  </RoleBasedRoute>
                }
              />

              {/* Gabaritos - Admin e Coordenador */}
              <Route
                path="/gabaritos"
                element={
                  <RoleBasedRoute allowedRoles={[ROLES.ADMIN, ROLES.COORDENADOR]}>
                    <PageMeta title="Gabaritos"><Gabaritos /></PageMeta>
                  </RoleBasedRoute>
                }
              />

              {/* Respostas dos Alunos - Admin/Coordenador */}
              <Route
                path="/respostas-alunos"
                element={
                  <RoleBasedRoute allowedRoles={[ROLES.ADMIN, ROLES.COORDENADOR]} deniedRoles={[ROLES.SECRETARIO]}>
                    <PageMeta title="Respostas dos Alunos"><RespostasAlunos /></PageMeta>
                  </RoleBasedRoute>
                }
              />

              {/* Simulador Financeiro - Admin, Gestor, Coordenador */}
              <Route
                path="/simulador-financeiro"
                element={
                  <RoleBasedRoute allowedRoles={[ROLES.ADMIN]}>
                    <PageMeta title="Simulador Financeiro"><SimuladorFinanceiro /></PageMeta>
                  </RoleBasedRoute>
                }
              />

              {/* IDEB - Municipal e por Escola (tabs) */}
              <Route
                path="/ideb-municipal"
                element={
                  <RoleBasedRoute allowedRoles={[ROLES.ADMIN, ROLES.COORDENADOR, ROLES.SUPERVISOR, ROLES.GESTOR, ROLES.REFERENCIA_TECNICA]}>
                    <PageMeta title="IDEB"><IdebMunicipal /></PageMeta>
                  </RoleBasedRoute>
                }
              />

              {/* Descritores - todos os usuarios autenticados */}
              <Route
                path="/descritores"
                element={
                  <RoleBasedRoute allowedRoles={[ROLES.ADMIN, ROLES.SUPERVISOR, ROLES.COORDENADOR, ROLES.GESTOR, ROLES.REFERENCIA_TECNICA, ROLES.PROFESSOR]}>
                    <PageMeta title="Descritores"><Descritores /></PageMeta>
                  </RoleBasedRoute>
                }
              />

              {/* Meu Perfil - qualquer usuário autenticado */}
              <Route
                path="/perfil"
                element={<PageMeta title="Meu Perfil"><Perfil /></PageMeta>}
              />
            </Route>

            {/* 404 - Página não encontrada */}
            <Route path="*" element={<PageMeta title="Página não encontrada"><NotFound /></PageMeta>} />
          </Routes>
        </Router>
        </SelectedCityProvider>
      </ThemeProvider>
    </AuthProvider>

  );
}

export default App;
